/**
 * @fileoverview Hook de UI Layer para el acordeón de detalles del cliente en Inbox.
 * @module useCustomerDetailsAccordionUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 2.1.0
 */
import { useCallback, useState } from 'react';

/**
 * Hook especializado de UI para CustomerDetailsAccordionMC.
 *
 * - Gestiona el estado del panel abierto.
 * - Provee configuración de ícono y handler para cada panel.
 *
 * @returns {Object} Objeto con estado y configuración de UI para el acordeón
 * @returns {string|null} openPanel - Key del panel abierto
 * @returns {Function} handlePanelToggle - Handler para abrir/cerrar paneles
 * @returns {Function} getIconConfig - Devuelve configuración de ícono para un panel
 *
 * @example
 * const { openPanel, handlePanelToggle, getIconConfig } = useCustomerDetailsAccordionUI();
 * <IconMC {...getIconConfig(panel.key)} onClick={() => handlePanelToggle(panel.key)} />
 */
function useCustomerDetailsAccordionUI(defaultPanel = null) {
  const [openPanel, setOpenPanel] = useState(defaultPanel);

  /**
   * Handler memoizado para abrir/cerrar paneles
   */
  const handlePanelToggle = useCallback((key) => {
    setOpenPanel((prev) => (prev === key ? null : key));
  }, []);

  /**
   * Devuelve configuración de ícono para el panel
   * @param {string} key - Key del panel
   * @returns {Object} Configuración para IconMC
   */
  const getIconConfig = useCallback((key) => ({
    name: openPanel === key ? 'arrow-ios-upward-outline' : 'arrow-ios-downward-outline',
    size: 'base'
  }), [openPanel]);

  return {
    openPanel,
    handlePanelToggle,
    getIconConfig
  };
}

export default useCustomerDetailsAccordionUI;
